"use client";

import { Trans } from "@lingui/react/macro";
import { FragmentOf, readFragment } from "gql.tada";
import { CalendarDaysIcon, PlusIcon, SettingsIcon } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  SidebarGroupAction,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { IW_POLICIES } from "@/lib/authorization/policies/index-worker";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import CreateProjectDialog from "../../dialog/create-project-dialog";
import CollapsibleProjectGroup from "./collapsible-project-group";
import { IndexWorkerProjectItemFragment } from "./fragments";
import IwSortableProjects from "./iw-sortable-projects";
import SidebarMenuButtonWithPath from "./sidebar-menu-button-with-path";
import { SidebarSearchUnfilteredOnly } from "./sidebar-search-context";
import type { CollapsibleState } from "./types";

type Props = {
  workspaceId: string;
  projectsData: ReadonlyArray<
    FragmentOf<typeof IndexWorkerProjectItemFragment>
  >;
  initialGroupState: CollapsibleState;
  workspaceUsedColors?: string[];
};

export default function IndexWorkerSidebarGroup({
  workspaceId,
  projectsData,
  initialGroupState,
  workspaceUsedColors,
}: Props) {
  const currentPathname = usePathname();
  const checkPolicy = useCheckPolicy();
  const validProjectsData = projectsData || [];

  // Index Workerデータのキーを生成（Index Worker名の変更を検知するため）
  const dataKey = validProjectsData
    .map((data) => {
      const project = readFragment(IndexWorkerProjectItemFragment, data);
      if (project?.indexWorkers) {
        return project.indexWorkers
          .map((iw) => `${iw.id}:${iw.name}`)
          .join(",");
      }
      return "";
    })
    .join("|");

  const canEditProjects = checkPolicy(IW_POLICIES["iw-project:edit"]).ok;
  const canEditSearchConsole = checkPolicy(
    IW_POLICIES["iw-search-console:edit"],
  ).ok;

  return (
    <CollapsibleProjectGroup
      groupType="iw"
      title={<Trans>INDEX WORKER</Trans>}
      initialState={initialGroupState}
      action={
        canEditProjects ? (
          <CreateProjectDialog
            workspaceId={workspaceId}
            type="iw"
            workspaceUsedColors={workspaceUsedColors}
            trigger={
              <SidebarGroupAction>
                <PlusIcon className="w-4 h-4" />
                <span className="sr-only">
                  <Trans>New Project</Trans>
                </span>
              </SidebarGroupAction>
            }
          />
        ) : undefined
      }
    >
      <SidebarMenu>
        <SidebarSearchUnfilteredOnly>
          <SidebarMenuItem>
            <SidebarMenuButtonWithPath
              activePattern={`.*/${encodeURIComponent(
                workspaceId,
              )}/iw/job-plan`}
              asChild
            >
              <Link href={`/${encodeURIComponent(workspaceId)}/iw/job-plan`}>
                <CalendarDaysIcon className="w-4 h-4" />
                <Trans>Job execution plan</Trans>
              </Link>
            </SidebarMenuButtonWithPath>
          </SidebarMenuItem>
        </SidebarSearchUnfilteredOnly>
        <IwSortableProjects
          key={dataKey}
          workspaceId={workspaceId}
          projectsData={validProjectsData}
          currentPathname={currentPathname}
        />
        {canEditSearchConsole ? (
          <SidebarSearchUnfilteredOnly>
            <SidebarMenuItem>
              <SidebarMenuButtonWithPath
                activePattern={`.*/${encodeURIComponent(
                  workspaceId,
                )}/settings/sc-properties`}
                asChild
              >
                <Link
                  href={`/${encodeURIComponent(
                    workspaceId,
                  )}/settings/sc-properties`}
                >
                  <SettingsIcon className="w-4 h-4" />
                  <Trans>Search Console Properties</Trans>
                </Link>
              </SidebarMenuButtonWithPath>
            </SidebarMenuItem>
          </SidebarSearchUnfilteredOnly>
        ) : null}
      </SidebarMenu>
    </CollapsibleProjectGroup>
  );
}
